/**
 * Hằng số / enum tham chiếu cho trang Hội thoại — port 1:1 từ
 * `vpage-fe1/src/app/models/conversation.ts` để giữ contract với BE và
 * tránh duplicate logic ở nhiều nơi.
 *
 * Các định nghĩa thuần kiểu `IConversation`, filter… nằm ở [conversation.model.ts](./conversation.model.ts).
 * File này dành cho **runtime constants** (giá trị enum, label dictionary…).
 */

import { CHANNEL } from './channel.model';
import { CHANNEL_NAME } from '@shared/models/conversation';

// ─── Trạng thái / loại tin nhắn ──────────────────────────────────────────────

export const MSG_STATUS = {
  SENT: 1,
  ERROR: 2,
  SENDING: 3,
} as const;
export type MsgStatusValue = typeof MSG_STATUS[keyof typeof MSG_STATUS];

export const MSG_TYPE = {
  TEXT: 1,
  PRODUCT: 2,
  VOUCHER: 3,
  ORDER: 4,
  ADDRESS: 5,
  FEEDBACK: 6,
  TEMPLATE: 7,
  STICKER: 8,
  QUICK_REPLIES: 9,
} as const;
export type MsgTypeValue = typeof MSG_TYPE[keyof typeof MSG_TYPE];

// ─── Activity log ────────────────────────────────────────────────────────────

export const ACTIVITY_TYPE = {
  VIEW: 1,
  TAG_ADD: 2,
  TAG_DEL: 3,
  HAS_REPLIED: 4,
  EDIT_MESSAGE: 5,
} as const;

/** Mã thành công chuẩn trong response của các API hội thoại. */
export const CONVERSATION_API_RESULT = {
  SUCCESS: 1,
} as const;

/** Giá trị BE dùng khi đánh dấu hội thoại thuộc luồng partner/affiliate. */
export const CONVERSATION_PARTNER_FLAG = {
  NO: 0,
  YES: 1,
} as const;

/** Contract ghi và truy vấn lịch sử xem hội thoại. */
export const CONVERSATION_VIEW = {
  LOG_VIEW: 1,
  HISTORY_TYPE: 2,
} as const;

// ─── Nguồn tạo tin nhắn (CREATED_BY_TYPE) ────────────────────────────────────

export const CREATED_BY_TYPE = {
  USER: 1,
  AUTO_REPLY: 2,
  CHASE_SCRIPT: 3,
  BULK_MESSAGE: 4,
  ORDER_CARE: 5,
  OTHER_APP: 6,
  CHATBOT_SCRIPT: 7,
  CHATBOT_AI: 8,
  META_BIZ_AI: 9,
  BIZ_AI_FORWARD: 10,
} as const;

// ─── Chatbot status (hiển thị icon ChatGPT/BizAI ở list) ─────────────────────

export const CHATBOT_STATUS = {
  OFF: 1,
  BIZ_AI_PROCESSING: 2,
  BIZ_AI_TRANSFERED: 3,
} as const;

// ─── Mute ───────────────────────────────────────────────────────────────────

/**
 * Giá trị mute theo BE — gửi nguyên vào `muteType` param.
 * - `MUTE_ONE_HOUR` / `MUTE_FOUR_HOURS`: số giây tới timestamp mute hết hạn.
 * - `MUTE_FOREVER` : -1.
 * - `MUTE_UNTIL_8AM`: chuỗi `"until8AM"` — BE tự tính 8h sáng hôm sau.
 */
export const MUTE_TYPE = {
  MUTE_ONE_HOUR: 3600,
  MUTE_FOUR_HOURS: 14400,
  MUTE_FOREVER: -1,
  MUTE_UNTIL_8AM: 'until8AM',
} as const;

export type MuteTypeValue = typeof MUTE_TYPE[keyof typeof MUTE_TYPE];

// ─── Quick replies ──────────────────────────────────────────────────────────

export const QUICK_REPLIES_TYPE = {
  TEXT: 1,
  PHONE_NUMBER: 2,
  EMAIL: 3,
} as const;

// setting gửi kèm yêu cầu địa chỉ (copy từ FE cũ)
export const REQUEST_INFO = {
  /** Xin số điện thoại và địa chỉ giao hàng. */
  ADDRESS: 1,
  /** Không xin thông tin (giá trị được lưu ở quick message legacy). */
  NONE: 2,
  /** Xin số điện thoại. */
  PHONE: 3,
} as const;

// ─── Message text style (định dạng b/i/u/s/màu/size cho Zalo Personal) ───────

export const MESSAGE_TEXT_STYLE = {
  bold: 'b',
  italic: 'i',
  underline: 'u',
  strikeThrough: 's',
  red: 'c_db342e',
  orange: 'c_f27806',
  yellow: 'c_f7b503',
  green: 'c_15a85f',
  small: 'f_13',
  big: 'f_18',
  unorderedList: 'lst_1',
  orderedList: 'lst_2',
  indent: 'ind_$',
} as const;

// ─── Note color (ghi chú khách hàng có màu) ─────────────────────────────────

export interface INoteColorOption {
  key: string;
  label: string;
  configs: { color: string; background: string; border: string } | null;
}

// ─── Business Setting CRM (POS iframe) ─────────────────────────────────────────
/** @see `vpage-fe1 BusineessSetting` — `src/app/models/setting.ts` */
export const BUSINESS_SETTING_CRM = {
  TYPE_SETTING_CRM: 22, // Cài đặt CRM => hiện tại chỉ có iframe tạo đơn hàng
  CRM_ADD_POS_V1: 26, // POS v1 (crmadd)
  CRM_ADD_POS_V2: 23, // POS v2 (crmadd2) — mặc định
  CRM_ADD_CUSTOM_LINK: 24, // Custom link từ cài đặt
} as const;
export type BusinessSettingCrmValue = typeof BUSINESS_SETTING_CRM[keyof typeof BUSINESS_SETTING_CRM];

export const NOTE_COLOR_OPTIONS: INoteColorOption[] = [
  { key: 'default', label: 'Mặc định', configs: null },
  {
    key: 'blue',
    label: 'Xanh nước biển',
    configs: { color: '#1565c0', background: '#e3f2fd', border: '1px solid #64b5f6' }
  },
  { key: 'red', label: 'Đỏ', configs: { color: '#c62828', background: '#ffebee', border: '1px solid #ef9a9a' } },
  { key: 'green', label: 'Xanh lá cây', configs: { color: '#2e7d32', background: '#e8f5e9', border: '1px solid #81c784' } },
  { key: 'orange', label: 'Cam', configs: { color: '#ef6c00', background: '#fff3e0', border: '1px solid #ffb74d' } },
  { key: 'sky', label: 'Xanh da trời', configs: { color: '#00838f', background: '#e0f7fa', border: '1px solid #4dd0e1' } },
];

// ─── Zalo Personal group event ──────────────────────────────────────────────

export const ZALO_PERSONAL_GROUP_EVENT = {
  CREATED: 'zlps_gr_join',
  JOIN_REQUEST: 'zlps_gr_0',
  JOIN: 'zlps_gr_1',
  LEAVE: 'zlps_gr_2',
  REMOVE_MEMBER: 'zlps_gr_3',
  BLOCK_MEMBER: 'zlps_gr_4',
  UPDATE_SETTING: 'zlps_gr_5',
  UPDATE: 'zlps_gr_6',
  NEW_LINK: 'zlps_gr_7',
  ADD_ADMIN: 'zlps_gr_8',
  REMOVE_ADMIN: 'zlps_gr_9',
  NEW_PIN_TOPIC: 'zlps_gr_10',
  UPDATE_PIN_TOPIC: 'zlps_gr_11',
  REORDER_PIN_TOPIC: 'zlps_gr_12',
  UPDATE_BOARD: 'zlps_gr_13',
  REMOVE_BOARD: 'zlps_gr_14',
  UPDATE_TOPIC: 'zlps_gr_15',
  UNPIN_TOPIC: 'zlps_gr_16',
  REMOVE_TOPIC: 'zlps_gr_17',
  ACCEPT_REMIND: 'zlps_gr_18',
  REJECT_REMIND: 'zlps_gr_19',
  REMIND_TOPIC: 'zlps_gr_20',
  UNKNOWN: 'zlps_gr_21',
} as const;

export const ZALO_PERSONAL_CONST_GROUP_EVENT = {
  JOIN_REQUEST: 0,
  JOIN: 1,
  LEAVE: 2,
  REMOVE_MEMBER: 3,
  BLOCK_MEMBER: 4,
  UPDATE_SETTING: 5,
  UPDATE: 6,
  NEW_LINK: 7,
  ADD_ADMIN: 8,
  REMOVE_ADMIN: 9,
  NEW_PIN_TOPIC: 10,
  UPDATE_PIN_TOPIC: 11,
  REORDER_PIN_TOPIC: 12,
  UPDATE_BOARD: 13,
  REMOVE_BOARD: 14,
  UPDATE_TOPIC: 15,
  UNPIN_TOPIC: 16,
  REMOVE_TOPIC: 17,
  ACCEPT_REMIND: 18,
  REJECT_REMIND: 19,
  REMIND_TOPIC: 20,
  UNKNOWN: 21,
} as const;

/** System assignee IDs reserved for conversation distribution and visibility. */
export {
  ASSIGNEE_ID_STOP_SPLIT,
  ASSIGNEE_ID_ALL_STAFF_VIEW,
  SYSTEM_ASSIGNEE_IDS,
} from '@shared/models/conversation';

// ─── Split conversation result codes ────────────────────────────────────────

export const CODE_RETURN_SPLIT = {
  INVALID_CONVERSATION: -1,
  INVALID_PAGE: -2,
  INVALID_BUSINESS: -3,
  INVALID_SETTING: -4,
  INVALID_LIMIT_TIME: -5,
  INVALID_IGNORE_LIVE: -6,
  INVALID_IGNORE_POSTS: -7,
  INVALID_SETTING_MODE: -8,
  INVALID_SETTING_PAGE: -9,
  IGNORE_NEW_SPLIT: -10,
  NO_STAFF: -11,
  INVALID_IGNORE_MANUAL: -12,
  SPLIT_SUCCESS: 1,
} as const;

export const CODE_RETURN_SPLIT_NAME: Record<number, string> = {
  [CODE_RETURN_SPLIT.INVALID_CONVERSATION]: 'Hội thoại không hợp lệ',
  [CODE_RETURN_SPLIT.INVALID_PAGE]: 'Page không hợp lệ',
  [CODE_RETURN_SPLIT.INVALID_BUSINESS]: 'Page chưa được gắn vào doanh nghiệp',
  [CODE_RETURN_SPLIT.INVALID_SETTING]: 'Chưa setting',
  [CODE_RETURN_SPLIT.INVALID_LIMIT_TIME]: 'Giới hạn thời gian chia hoạt động',
  [CODE_RETURN_SPLIT.INVALID_IGNORE_LIVE]: 'Bỏ qua bài live',
  [CODE_RETURN_SPLIT.INVALID_IGNORE_POSTS]: 'Bỏ qua bài posts',
  [CODE_RETURN_SPLIT.INVALID_SETTING_MODE]: 'Chưa chọn chế độ chia',
  [CODE_RETURN_SPLIT.INVALID_SETTING_PAGE]: 'Page không thuộc danh sách áp dụng chia',
  [CODE_RETURN_SPLIT.IGNORE_NEW_SPLIT]: 'Bỏ qua, không check chia mới',
  [CODE_RETURN_SPLIT.NO_STAFF]: 'Không tìm thấy nhân viên nào',
  [CODE_RETURN_SPLIT.INVALID_IGNORE_MANUAL]: 'Bỏ qua đang áp dụng chia thủ công',
  [CODE_RETURN_SPLIT.SPLIT_SUCCESS]: 'Chia thành công',
};

/** Số ngày giới hạn cho phép lọc chia lại. */
export const LIMIT_DAY_RE_SPLIT = 7;

// ─── Từ khóa tự động chặn / cảnh báo [§4.10] ────────────────────────────────

/**
 * Kết quả kiểm tra từ khóa nhạy cảm — trả về bởi ChatCoreService.checkAutoBlockKeywords().
 * mode: 0 = không khớp · 1 = cảnh báo (hiện dialog warning-key-words) · 2 = chặn (không gửi)
 */
export interface IAutoBlockKeywordsResult {
  mode: 0 | 1 | 2;
  /** Từ khóa đầu tiên tìm thấy trong văn bản; null nếu không có */
  keyword: string | null;
}

// ─── Khung tin nhắn order (lưu thông tin đơn) ────────────────────────────────

export const MSG_ORDER_INFORMATION = [
  'phone',
  'name',
  'address_line_1',
  'administrative_area',
  'subadministrative_area',
  'locality',
  'country',
  'shipping_notes',
  'postal_code',
] as const;

// ─── Channel tabs (UI sidebar filter theo kênh) ─────────────────────────────

export interface IChannelTab {
  id: number;
  name: string;
  iconOptions: { class?: string; width?: number; height?: number };
}

export const CHANNEL_TABS: IChannelTab[] = [
  { id: CHANNEL.FACEBOOK, name: CHANNEL_NAME[CHANNEL.FACEBOOK], iconOptions: { class: 'f-24 mr-8' } },
  { id: CHANNEL.INSTAGRAM, name: CHANNEL_NAME[CHANNEL.INSTAGRAM], iconOptions: { class: 'f-24 mr-8' } },
  { id: CHANNEL.ZALO, name: CHANNEL_NAME[CHANNEL.ZALO], iconOptions: { width: 21, height: 21, class: 'br-2 mr-8' } },
  { id: CHANNEL.ZALO_PERSONAL, name: CHANNEL_NAME[CHANNEL.ZALO_PERSONAL], iconOptions: { width: 21, height: 21, class: 'br-2 mr-8' } },
  { id: CHANNEL.SHOPEE, name: CHANNEL_NAME[CHANNEL.SHOPEE], iconOptions: { width: 21, height: 21, class: 'mr-8' } },
  { id: CHANNEL.TIKTOK, name: CHANNEL_NAME[CHANNEL.TIKTOK], iconOptions: { width: 21, height: 21, class: 'br-2 mr-8' } },
  { id: CHANNEL.TIKTOK_BUSINESS, name: CHANNEL_NAME[CHANNEL.TIKTOK_BUSINESS], iconOptions: { width: 21, height: 21, class: 'br-2 mr-8' } },
  { id: CHANNEL.THREADS, name: CHANNEL_NAME[CHANNEL.THREADS], iconOptions: { width: 21, height: 21, class: 'br-2 mr-8' } },
  { id: CHANNEL.WHATSAPP, name: CHANNEL_NAME[CHANNEL.WHATSAPP], iconOptions: { width: 21, height: 21, class: 'mr-8' } },
  { id: CHANNEL.LAZADA, name: CHANNEL_NAME[CHANNEL.LAZADA], iconOptions: { width: 21, height: 21, class: 'br-2 mr-8' } },
];

// ─── Hằng số chuỗi loại tin nhắn (runtime mirror của IChatMessageType) ───────

/** Hằng số chuỗi loại tin nhắn — ánh xạ union type IChatMessageType trong message.model.ts */
export const MSG_TYPE_STRING = {
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  FILE: 'FILE',
  PRODUCT: 'PRODUCT',
  ORDER: 'ORDER',
  VOUCHER: 'VOUCHER',
  STICKER: 'STICKER',
  SYSTEM: 'SYSTEM',
  VOICE: 'VOICE',
  VIDEO: 'VIDEO',
  COMMAND: 'COMMAND',
  LOCATION: 'LOCATION',
  CALLING: 'CALLING',
  SHARE: 'SHARE',
  CONTACT: 'CONTACT',
  GENERIC: 'GENERIC',
  BUTTON: 'BUTTON',
  POST: 'POST',
  AD_REFERRAL: 'AD_REFERRAL',
  UTILITY: 'UTILITY',
  IMAGE_WITH_TEXT: 'IMAGE_WITH_TEXT',
  ECOM_PENDING: 'ECOM_PENDING',
  SHOPEE_BUNDLE: 'SHOPEE_BUNDLE',
  SHARE_POST: 'SHARE_POST',
  INTERACTIVE: 'INTERACTIVE',
} as const;
export type MsgTypeString = typeof MSG_TYPE_STRING[keyof typeof MSG_TYPE_STRING];

/** Hằng số chuỗi trạng thái tin nhắn — ánh xạ union type IChatMessage.status */
export const MSG_STATUS_STRING = {
  SENDING: 'SENDING',
  SENT: 'SENT',
  ERROR: 'ERROR',
} as const;
export type MsgStatusString = typeof MSG_STATUS_STRING[keyof typeof MSG_STATUS_STRING];

// ─── Hằng số postMessage cho Extension ──────────────────────────────────────

/** Định danh nguồn gửi postMessage của Extension */
export const EXT_ORIGIN = {
  WEB: 'CHAT_WEB',
  EXTENSION: 'NHANH_EXTENSION',
} as const;

/** Chuỗi loại message gửi/nhận qua Extension postMessage */
export const EXT_MSG = {
  CHECK_VERSION: 'NHANH_CHECK_EXTENSION_VERSION',
  EXTENSION_LOADED: 'NHANH_EXTENSION_LOADED',
  EXTENSION_VERSION: 'NHANH_EXTENSION_VERSION',
  REPLY_INBOX_PHOTO: 'NHANH_REPLY_INBOX_PHOTO',
  REPLY_SUCCESS: 'NHANH_REPLY_INBOX_PHOTO_SUCCESS',
  REPLY_FAILURE: 'NHANH_REPLY_INBOX_PHOTO_FAILURE',
  WS_CONTEXT_SUCCESS: 'NHANH_GET_BUSINESS_WS_CONTEXT_SUCCESS',
  WS_CONTEXT_FAILURE: 'NHANH_GET_BUSINESS_WS_CONTEXT_FAILURE',
  CACHE_MAPPING_DATA: 'NHANH_CACHE_MAPPING_DATA',
  GET_GLOBAL_USER_ID: 'NHANH_GET_GLOBAL_USER_ID',
  GLOBAL_USER_ID_SUCCESS: 'NHANH_GET_GLOBAL_USER_ID_SUCCESS',
  GET_FB_USER_INFO: 'NHANH_GET_FB_USER_INFO',
  FB_USER_INFO_SUCCESS: 'NHANH_GET_FB_USER_INFO_SUCCESS',
  ADD_REACTION_SUCCESS: 'NHANH_ADD_REACTION_MESSAGE_SUCCESS',
  PREINITIALIZE_PAGES: 'NHANH_PREINITIALIZE_PAGES',
  GET_BUSINESS_WS_CONTEXT: 'NHANH_GET_BUSINESS_WS_CONTEXT',
} as const;

/** Platform string identifiers cho extension routing */
export const PLATFORM = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
} as const;

// ─── Cấu hình gửi tin / hàng đợi / thử lại ─────────────────────────────────

export const SEND_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_BASE_DELAY: 1000,
  RETRY_MAX_DELAY: 10000,
  DEBOUNCE_MS: 300,
  EXTENSION_TIMEOUT: 30000,
  EXTENSION_TASK_TTL: 60,
} as const;

/** Khóa localStorage */
export const STORAGE_KEY = {
  SEND_PHOTO_MODES: 'sendPhotoModes',
  IMAGE_LIBRARY_SEND_PRIORITY: 'imageLibrarySendPriority',
} as const;

/** Giá trị chế độ gửi ảnh (sendPhotoModes) */
export const SEND_PHOTO_MODE = {
  BATCH: '1',
  INDIVIDUAL: '2',
} as const;

/** Sinh ID tạm cho optimistic message */
export function generateTempId(prefix = 'tmp'): string {
  return `${prefix}_${Math.random().toString(16).slice(2)}`;
}

/** Sinh fallback ID khi BE không trả id */
export function generateFallbackId(prefix: string): string {
  return `${prefix}_${Date.now()}`;
}

// ─── Nhãn hiển thị tin nhắn cuối (LastMessage) ─────────────────────────────

export const LAST_MESSAGE_LABEL = {
  REQUEST_ADDRESS: '[xin địa chỉ]',
  REQUEST_FEEDBACK: '[xin đánh giá]',
  STICKER: '[Sticker]',
  PHOTO: '[Photo]',
  VIDEO: '[Video]',
  AUDIO: '[Audio]',
  FILE: '[File]',
  ATTACHMENT: '[attachment]',
} as const;

// ─── Send error messages (Vietnamese) ────────────────────────────────────────

export const SEND_ERROR_MESSAGES = {
  SEND_FAILED: 'conversation.error.sendFailed',
  SEND_FAILED_SHORT: 'conversation.error.sendFailed',
  UNKNOWN_ERROR: 'conversation.error.unknown',
  EXT_TIMEOUT: 'conversation.error.extensionTimeout',
  EXT_TOO_OLD: 'conversation.error.extensionTooOld',
  EXT_IG_FILE_BLOCKED: 'conversation.error.extensionIgFileBlocked',
  EXT_FILE_BLOCKED: 'conversation.error.extensionFileBlocked',
  UPLOAD_FAILED: 'conversation.error.uploadFailedDetail',
  FB_UPLOAD_FAILED: 'conversation.error.fbUploadFailed',
  TIKTOK_VOUCHER_RULE: 'conversation.error.tiktokVoucherRule',
} as const;

// ─── Conversation action strings ────────────────────────────────────────────

export const CONVERSATION_ACTION_STR = {
  REVOKE: 'revoke',
  HIDE_COMMENT: 'hidecomment',
  LIKE_COMMENT: 'likecomment',
  RELOAD_MEDIA: 'reloadmedia',
} as const;

// ─── Chiến lược thử lại (retry) ────────────────────────────────────────────

export type RetryStrategy = 'none' | 'queue' | 'socket' | 'fallback_api';

/** Mã lỗi Facebook có thể thử lại qua socket (dùng chung với extension-send.service) */
export const FB_RETRYABLE_ERROR_CODES = ['1357031', '1357032', '1545012'] as const;

/** Giới tính khách hàng (map từ gender string của FB) */
export const GENDER = {
  MALE: 1,
  FEMALE: 2,
  NONBINARY: 3,
  OTHER: 4,
} as const;
export type GenderValue = typeof GENDER[keyof typeof GENDER];
